import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import {VRGroupService} from '../../vrgroup.service';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {BaseComponent} from '../../../../components/general/base/base.component';
import {TrainFidelity} from '../../common/train-fidelity';
import {subHours} from 'date-fns';
import {_, parseToDate} from '../../../../shared/utils';
import {format} from 'date-fns/esm';
import {forEach, groupBy} from 'lodash-es';
import {AppStabilityService} from '../../../../shared/app-stability.service';
import {FidelityColorThreshold} from './fidelity-color-threshold-type';
import { curveBasis } from 'd3-shape';
import { PercentageCircleComponent } from '~app/components/percentage-circle/percentage-circle.component';
import { TranslateService } from '@ngx-translate/core';

/**
 * Extended `TrainFidelity` variant with extra properties for data used internally by `TrainFidelityDbComponent`
 */
export interface AugmentedTrainFidelity extends TrainFidelity {
  color?: string;
  shortLabel?: string;
  longLabel?: string;
  results?: any;
}

@Component({
  selector: 'qm-vrgroup-train-fidelity-db',
  templateUrl: './train-fidelity-db.component.html',
  styleUrls: ['./train-fidelity-db.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TrainFidelityDbComponent extends BaseComponent implements OnInit, OnDestroy {

  public data: AugmentedTrainFidelity[];

  public graphWidth: number = PercentageCircleComponent.pixelSizes.normal;
  public graphHeight: number = PercentageCircleComponent.pixelSizes.normal;

  private history: any[];

  public graphColors: any = {
    domain: ['var(--color-success-base)', 'var(--color-warning-base)', 'var(--color-danger-base)']
  };

  // a d3 curve function passed to ngx-charts for line smoothing
  public curveFunction = curveBasis;

  /**
   * Subscription manager
   * @type {Subject<any>}
   */
  private ngUnsubscribe: Subject<void> = new Subject();
  private fetchHistoryTimer: any;

  constructor(private vrGroupService: VRGroupService,
              private changeDetector: ChangeDetectorRef,
              private appStability: AppStabilityService,
              private translateService: TranslateService) {
    super();
    this.name = 'TrainFidelityDb';
  }

  ngOnInit() {
    this.vrGroupService.trainFidelity$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(result => {
      if (!result || result.length < 3) {
        return;
      }
      this.data = result;
      this.augmentData();
      this.changeDetector.markForCheck();
    });
    this.loading = null;
    this.init();
    this.appStability.whenFirstStable().then(() => {
      this.fetchHistoryTimer = setInterval(() => {
        this.fetchHistory();
        this.augmentData();
      }, 1000 * 120);
    });
  }

  /**
   * Angular destructor function
   */
  ngOnDestroy() {
    if (this.fetchHistoryTimer) {
      clearInterval(this.fetchHistoryTimer);
    }
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private async fetchHistory() {
    const end = new Date();
    const start = subHours(end, 3);
    this.loading = await this.vrGroupService.getFidelityHistory(start, end);
    if (this.loading.isOk) {
      this.history = this.loading.data.data;
    }
    this.changeDetector.markForCheck();
  }

  private getGraphColor(): string[] {
    /*
    if (this.selected.value >= 92) {
      return window.getComputedStyle(document.body).getPropertyValue('--color-success-base');
    } else if (this.selected.value >= 80) {
      return window.getComputedStyle(document.body).getPropertyValue('--color-warning-base');
    } else {
      return window.getComputedStyle(document.body).getPropertyValue('--color-danger-base');
    }
    */
    return [
      window.getComputedStyle(document.body).getPropertyValue('--color-danger-base'),
      window.getComputedStyle(document.body).getPropertyValue('--color-danger-base'),
      window.getComputedStyle(document.body).getPropertyValue('--color-danger-base'),
      window.getComputedStyle(document.body).getPropertyValue('--color-danger-base'),
      window.getComputedStyle(document.body).getPropertyValue('--color-danger-base'),
      window.getComputedStyle(document.body).getPropertyValue('--color-danger-base'),
      window.getComputedStyle(document.body).getPropertyValue('--color-danger-base'),
      window.getComputedStyle(document.body).getPropertyValue('--color-danger-base'),
      window.getComputedStyle(document.body).getPropertyValue('--color-danger-base'),
      window.getComputedStyle(document.body).getPropertyValue('--color-danger-base'),
      window.getComputedStyle(document.body).getPropertyValue('--color-warning-base'),
      window.getComputedStyle(document.body).getPropertyValue('--color-success-base')
    ];
  }

  /// updates the extra properties of `AugmentedTrainFidelity` in `this.data`
  public augmentData(): any[] {
    if (!this.data) {
      return;
    }

    for (const fidelity of Object.values(this.data)) {
      fidelity.color = this.getColorForPercentage(fidelity);

      fidelity.shortLabel = {
        'LONGDISTANCE': _('Longdist.'),
        'COMMUTER': _('Commute'),
        'FREIGHT': _('Freight'),
      }[fidelity.id];

      fidelity.longLabel = {
        'LONGDISTANCE': _('Long distance trains'),
        'COMMUTER': _('Commuter trains'),
        'FREIGHT': _('Freight trains'),
      }[fidelity.id];
    }
    this.changeDetector.markForCheck();

    if (!this.history) {
      return;
    }

    for (const fidelity of Object.values(this.data)) {
      this.graphColors = {
        domain: this.getGraphColor()
      };
      const graphData = [
        {
          name: this.translateService.instant(fidelity.longLabel),
          series: []
        }
      ];
      let temp: any = [];
      forEach(this.history, (o: any) => {
        const createdAt = format(parseToDate(o.createdAt), 'HH:mm');
        temp.push({createdAt: createdAt, value: o[fidelity.id].value});
      });
      temp = groupBy(temp, 'createdAt');
      const keys = Object.keys(temp);
      forEach(keys, (o: string) => {
        if (temp[o].length === 1) {
          graphData[0].series.push({value: temp[o][0].value, name: o});
        } else {
          let mean = 0;
          for (const row of temp[o]) {
            mean += row.value;
          }
          mean = mean / temp[o].length;
          graphData[0].series.push({value: mean, name: o});
        }
      });
      fidelity.results = graphData;
    }
    this.changeDetector.markForCheck();
  }

  private async init() {
    await this.fetchHistory();
    const row = this.history[this.history.length - 1];
    this.data = [
      {
        value: row.LONGDISTANCE.value,
        sum: row.LONGDISTANCE.sum,
        count: row.LONGDISTANCE.count,
        id: 'LONGDISTANCE'
      },
      {
        value: row.COMMUTER.value,
        sum: row.COMMUTER.sum,
        count: row.COMMUTER.count,
        id: 'COMMUTER'
      },
      {
        value: row.FREIGHT.value,
        sum: row.FREIGHT.sum,
        count: row.FREIGHT.count,
        id: 'FREIGHT'
      }
    ];
    this.augmentData();
    this.changeDetector.markForCheck();
  }

  private getColorForPercentage(fidelity: TrainFidelity): string {
    const threshold = this.getColorThreshold(fidelity);

    if (fidelity.value >= threshold.thresholdGreen) {
      return 'success';
    } else if (fidelity.value >= threshold.thresholdYellow) {
      return 'warning';
    } else {
      return 'danger';
    }
  }

  private getColorThreshold(fidelity: TrainFidelity): FidelityColorThreshold {

      const thresholds: FidelityColorThreshold[] = [
        {
          fidelityId: 'LONGDISTANCE',
          thresholdGreen: 90,
          thresholdYellow: 87
        },
        {
          fidelityId: 'COMMUTER',
          thresholdGreen: 97,
          thresholdYellow: 94
        },
        {
          fidelityId: 'FREIGHT',
          thresholdGreen: 87.5,
          thresholdYellow: 85
        },
        {
          fidelityId: 'DEFAULT',
          thresholdGreen: 92,
          thresholdYellow: 80
        }
      ];

      const usedThreshold = thresholds.find(t => t.fidelityId === fidelity.id);

      return usedThreshold !== undefined
              ? usedThreshold
              : thresholds.find(t => t.fidelityId === 'DEFAULT');
  }

  public yAxisTickFormatting(i: number): string {
    return undefined;
  }

  public onResize(width: number, height: number): void {
    this.graphWidth = width - PercentageCircleComponent.pixelSizes.normal - 30;
    this.changeDetector.markForCheck();
  }
}
